"use client";
//? Library Imports ------------------------------------------------------------>
import { useSelector, useDispatch } from "react-redux";
import { useRouter } from "next/navigation";
import { useState, useEffect, useCallback } from "react";
import { Col, Tag, Radio, Space, message, Skeleton, Typography } from "antd";

//? Assets --------------------------------------------------------------------->
import { ArrowLeftOutlined, CloseOutlined } from "@ant-design/icons";

//? Modules -------------------------------------------------------------------->
import AccountStatus from "@/functions/Cookies/Cookies";
import CreateNewAddress from "./Modules/CreateNewAddress/CreateNewAddress";
import DynamicButton from "@/components/shared/Buttons/DynamicButton/DynamicButton";
import { customerLogout } from "@/functions/Cookies/Cookies";

//? Redux Reducers ------------------------------------------------------------->
import { logoutReducer } from "@/redux/Slices/accountSlice";
import Cookies from "js-cookie";
//? Library Constants ---------------------------------------------------------->
const { Text } = Typography;

export default function AddressBook({ setActiveMenu }) {
  const { comverse_customer_token } = AccountStatus();

  //? Redux States --------------------------------------------------------------->
  const dispatch = useDispatch();

  const StoreId = useSelector(
    (state) => state?.multiLocation?.defaultCountryId
  );

  const router = useRouter();
  const [value, setValue] = useState(null);
  const [Loading, setLoading] = useState(true);
  const [AddressList, setAddressList] = useState([]);
  const [AddNewAddress, setAddNewAddress] = useState(false);
  const [FormType, setFormType] = useState("create");
  const [SelectedAddress, setSelectedAddress] = useState({});

  const mainColor = Cookies.get("main_theme");
  const secondaryColor = Cookies.get("secondary_theme");
  const styleElement = document.createElement("style");

  styleElement.innerHTML = `
  .ant-radio-wrapper .ant-radio-checked .ant-radio-inner {
    border: 1px solid ${mainColor} !important;
  }
  .ant-radio-wrapper .ant-radio-checked .ant-radio-inner {
    background-color: ${mainColor} !important;
  }
  .ant-radio-wrapper:hover .ant-radio-inner{
    border-color: ${mainColor} !important;
  }
  
`;

  document.head.appendChild(styleElement);

  const selectAddress = (e) => {
    setValue(e.target.value);
  };

  const fetchCustomerAddresses = useCallback(async () => {
    if (comverse_customer_token) {
      try {
        const payload = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: comverse_customer_token,
          },
        };

        const res = await fetch(
          process.env.NEXT_PUBLIC_BACKEND +
            `/storefront/customer_address?storefront=${StoreId}`,
          payload
        );

        const CustomerAddresses = await res.json();

        if (!res.ok) {
          if (CustomerAddresses?.detail === "Invalid auth token") {
            customerLogout();
            dispatch(logoutReducer());
            router.push("/login");
          }
          //logout here
          throw new Error("Customer Address Api Failed!");
        }

        setAddressList(CustomerAddresses);
        setValue(CustomerAddresses[0]?.id);

        setLoading(false);

        return CustomerAddresses;
      } catch (error) {
        console.error("Customer Address Api Failed!", error);
        setTimeout(() => {
          setLoading(false);
        }, 1000);
        return null;
      }
    }
  }, [StoreId, comverse_customer_token]);

  const removeAddress = async (id) => {
    if (comverse_customer_token && id) {
      try {
        setLoading(true);
        const headers = {
          "Content-Type": "application/json",
        };
        if (comverse_customer_token) {
          headers.Authorization = comverse_customer_token;
        }

        const body = {
          id: id,
        };

        const payload = {
          method: "DELETE",
          headers: headers,
          body: JSON.stringify(body),
        };

        const res = await fetch(
          process.env.NEXT_PUBLIC_BACKEND +
            `/storefront/customer_address?storefront=${StoreId}`,
          payload
        );
        const data = await res.json();

        if (!res.ok) {
          throw new Error("Api Failed!");
        } else {
          setLoading(false);
          message.success(data?.detail);
          fetchCustomerAddresses();
          return data;
        }
      } catch (error) {
        console.error("Error:", error);

        setTimeout(() => {
          setLoading(false);
        }, 1000);
        return null;
      }
    }
  };

  const editAddress = async () => {
    if (comverse_customer_token) {
      try {
        const payload = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: comverse_customer_token,
          },
        };

        const res = await fetch(
          process.env.NEXT_PUBLIC_BACKEND +
            `/storefront/customer_address?id=${value}&storefront=${StoreId}`,
          payload
        );

        const CustomerAddress = await res.json();

        if (!res.ok) {
          throw new Error("Customer Address Api Failed!");
        }

        if (CustomerAddress) {
          setLoading(false);
          setFormType("edit");
          setAddNewAddress(true);
          setSelectedAddress(CustomerAddress);
        }

        return CustomerAddress;
      } catch (error) {
        console.error("Customer Address Api Failed!", error);
        setTimeout(() => {
          setLoading(false);
        }, 1000);
        return null;
      }
    }
  };

  useEffect(() => {
    fetchCustomerAddresses();
  }, [fetchCustomerAddresses]);

  return (
    <Skeleton loading={Loading}>
      <Col className="AddressBook padding20">
        {AddNewAddress ? (
          <Col>
            <CreateNewAddress
              FormType={FormType}
              setFormType={setFormType}
              SelectedAddress={SelectedAddress}
              setAddNewAddress={setAddNewAddress}
              fetchCustomerAddresses={fetchCustomerAddresses}
            />
          </Col>
        ) : (
          <Col>
            <Col className="flexBetween">
              <Space size="middle">
                <Col
                  style={{
                    background: "#FFFFFF",
                    width: "29px",
                    height: "30px",
                  }}
                  className="flexCenter cursorPointer"
                  onClick={() => setActiveMenu("dashboard")}
                >
                  <ArrowLeftOutlined />
                </Col>
                <Text className="font18">Address Book</Text>
              </Space>

              <Space>
                <DynamicButton
                  name="Add New Address"
                  className="addBtn"
                  function={() => setAddNewAddress(true)}
                />
                <DynamicButton
                  name="Edit"
                  className="editBtn"
                  function={() => editAddress()}
                />
              </Space>
            </Col>

            {AddressList?.length > 0 && (
              <Col className="marginTop15">
                <Col className="addressCarddd">
                  <Radio.Group onChange={selectAddress} value={value}>
                    {AddressList?.map((address, index) => {
                      return (
                        <div>
                          <Radio
                            key={index}
                            value={address?.id}
                            className="addressCard positionRelative"
                          >
                            <CloseOutlined
                              style={{
                                top: "10px",
                                right: "10px",
                                cursor: "pointer",
                                position: "absolute",
                              }}
                              onClick={() => removeAddress(address?.id)}
                            />
                            <Col className="flexBetween">
                              <Col>
                                <Text className="font14">
                                  {address?.full_name}&nbsp;
                                  {address?.primary_address ? (
                                    <span
                                      style={{
                                        color: "#B7B7B7",
                                        fontSize: "12px",
                                      }}
                                    >
                                      (Default)
                                    </span>
                                  ) : null}
                                </Text>
                                <br />
                                <Text className="font14Bold poppinsSemiBold">
                                  {address?.address}
                                </Text>
                                <br />
                                <Text className="font14Bold poppinsSemiBold">
                                  {address?.phone}
                                </Text>
                              </Col>

                              {/* <Col
                                style={{
                                  position: "absolute",
                                  right: "15px",
                                  top: "0",
                                }}
                              >
                                <Tag className="addressTag borderNone">
                                  {address?.tag}
                                </Tag>
                              </Col> */}
                            </Col>
                          </Radio>
                        </div>
                      );
                    })}
                  </Radio.Group>
                </Col>
              </Col>
            )}
          </Col>
        )}
      </Col>
    </Skeleton>
  );
}
